import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from './CaseStartForm.module.scss';
import RadioButtonGroup from "../../../../components/radioButtonGroup/RadioButtonGroup";
import TextField from "../../../../components/textField/TextField";
import TimePicker from "../../../../components/timePicker/TimePicker";
import Dropdown from "../../../../components/dropdown/Dropdown";
import PropTypes from "prop-types";
import { compareTimes } from "../../../../utils/timeUtils";
import { formatWeight } from "../../../../utils/stringUtils";
import MultiSelectDropdown from "../../../../components/multiSelectDropdown/MultiSelectDropdown";
import { useToasts } from "../../../../contexts/ToastContext";

const CaseStartForm = forwardRef(({
  formData,
  patientPositionsList = [],
  anatomicLocationOfProceduresList = [],
  maskManagementList = [],
  typeOfAnestheticList = [],
  supraGlotticAirwayList = [],
  onSave,
  disabled = false
}, ref) => {
  const [isSimulatedClinicalExperience, setIsSimulatedClinicalExperience] = useState(null);
  const [isSimulatedClinicalExperienceError, setIsSimulatedClinicalExperienceError] = useState('');
  const [caseCount, setCaseCount] = useState(null);
  const [caseCountError, setCaseCountError] = useState('');
  const [startTime, setStartTime] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [endTime, setEndTime] = useState('');
  const [endTimeError, setEndTimeError] = useState('');
  const [patientWeight, setPatientWeight] = useState(null);
  const [patientWeightError, setPatientWeightError] = useState('');
  const [patientPosition, setPatientPosition] = useState([]);
  const [patientPositionError, setPatientPositionError] = useState('');
  const [anatomicLocationOfProcedure, setAnatomicLocationOfProcedure] = useState([]);
  const [anatomicLocationOfProcedureError, setAnatomicLocationOfProcedureError] = useState('');
  const [maskManagement, setMaskManagement] = useState(null);
  const [maskManagementError, setMaskManagementError] = useState('');
  const [typeOfAnesthetic, setTypeOfAnesthetic] = useState(null);
  const [typeOfAnestheticError, setTypeOfAnestheticError] = useState('');
  const [supraGlotticAirwayDevice, setSupraGlotticAirwayDevice] = useState(null);
  const [supraGlotticAirwayDeviceError, setSupraGlotticAirwayDeviceError] = useState('');
  const [inhalationInduction, setInhalationInduction] = useState(null);
  const [inhalationInductionError, setInhalationInductionError] = useState('');
  const [ventilationByMask, setVentilationByMask] = useState(null);
  const [ventilationByMaskError, setVentilationByMaskError] = useState('');
  const { showToast } = useToasts();

  useEffect(() => {
    setIsSimulatedClinicalExperience(formData?.isSimulatedClinicalExperience);
    setCaseCount(formData?.caseCount || null);
    setStartTime(formData?.startTime || '');
    setEndTime(formData?.endTime || '');
    setPatientWeight(formData?.patientWeight || null);
    setPatientPosition(formData?.patientPosition || []);
    setAnatomicLocationOfProcedure(formData?.anatomicLocationOfProcedure || []);
    setMaskManagement(formData?.maskManagement || null);
    setTypeOfAnesthetic(formData?.typeOfAnesthetic || null);
    setSupraGlotticAirwayDevice(formData?.supraGlotticAirwayDevice || null);
    setInhalationInduction(formData?.inhalationInduction);
    setVentilationByMask(formData?.ventilationByMask);
  }, [formData]);

  const validateIsSimulatedClinicalExperience = () => {
    if(isSimulatedClinicalExperience === null) {
      return 'Simulated Clinical Experience Case is required';
    }
    return '';
  }

  const validateCaseCount = () => {
    if(!caseCount) {
      return 'Simulated Clinical Experience Case is required';
    }
    return '';
  }

  const validateStartTime = () => {
    if(!startTime) {
      return 'Start Time is required';
    }
    if(endTime && !compareTimes(startTime, endTime)) {
      return 'Start time cannot be later than or equal to end time';
    }
    return '';
  }

  const validateEndTime = () => {
    if(!endTime) {
      return 'End Time is required';
    }
    if(startTime && !compareTimes(startTime, endTime)) {
      return 'End time cannot be earlier than or equal to start time';
    }
    return '';
  }

  const validatePatientWeight = () => {
    if(!patientWeight) {
      return 'Patient Weight is required';
    }
    return '';
  }

  const validatePatientPosition = () => {
    if(patientPosition.length === 0) {
      return 'Patient Position is required';
    }
    return '';
  }

  const validateAnatomicLocationOfProcedure = () => {
    if(anatomicLocationOfProcedure.length === 0) {
      return 'Anatomic Location Of Procedure is required';
    }
    return '';
  }

  const validateMaskManagement = () => {
    if(!maskManagement) {
      return 'Mask Management is required';
    }
    return '';
  }

  const validateTypeOfAnesthetic = () => {
    if(!typeOfAnesthetic) {
      return 'Type of Anesthetic is required';
    }
    return '';
  }

  const validateSupraGlotticAirway = () => {
    if(!supraGlotticAirwayDevice) {
      return 'Supra Glottic Airway Device is required';
    }
    return '';
  }

  const validateInhalationInduction = () => {
    if(inhalationInduction === null) {
      return 'Inhalation Induction is required';
    }
    return '';
  }

  const validateVentilationByMask = () => {
    if(ventilationByMask === null) {
      return 'Ventilation by Mask is required';
    }
    return '';
  }

  const validateForm = () => {
    const isSimulatedClinicalExperienceValidationResult = validateIsSimulatedClinicalExperience();
    const caseCountValidationResult = validateCaseCount();
    const startTimeValidationResult = validateStartTime();
    const endTimeValidationResult = validateEndTime();
    const patientWeightValidationResult = validatePatientWeight();
    const patientPositionValidationResult = validatePatientPosition();
    const anatomicLocationOfProcedureValidationResult = validateAnatomicLocationOfProcedure();
    const maskManagementValidationResult = validateMaskManagement();
    const typeOfAnestheticValidationResult = validateTypeOfAnesthetic();
    const supraGlotticAirwayDeviceValidationResult = validateSupraGlotticAirway();
    const inhalationInductionValidationResult = validateInhalationInduction();
    const ventilationByMaskValidationResult = validateVentilationByMask();

    setIsSimulatedClinicalExperienceError(isSimulatedClinicalExperienceValidationResult);
    setCaseCountError(caseCountValidationResult);
    setStartTimeError(startTimeValidationResult);
    setEndTimeError(endTimeValidationResult);
    setPatientWeightError(patientWeightValidationResult);
    setPatientPositionError(patientPositionValidationResult);
    setAnatomicLocationOfProcedureError(anatomicLocationOfProcedureValidationResult);
    setMaskManagementError(maskManagementValidationResult);
    setTypeOfAnestheticError(typeOfAnestheticValidationResult);
    setSupraGlotticAirwayDeviceError(supraGlotticAirwayDeviceValidationResult);
    setInhalationInductionError(inhalationInductionValidationResult);
    setVentilationByMaskError(ventilationByMaskValidationResult);

    const errors = [
      isSimulatedClinicalExperienceValidationResult,
      caseCountValidationResult,
      startTimeValidationResult,
      endTimeValidationResult,
      patientWeightValidationResult,
      patientPositionValidationResult,
      anatomicLocationOfProcedureValidationResult,
      maskManagementValidationResult,
      typeOfAnestheticValidationResult,
      supraGlotticAirwayDeviceValidationResult,
      inhalationInductionValidationResult,
      ventilationByMaskValidationResult
    ];
  
    const errorCount = errors.filter(error => error !== '').length;
  
    return errorCount;
  }

  // Expose the function to the parent
  useImperativeHandle(ref, () => ({
    validateForm
  }));

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={styles.row}>
        <div className={styles.column}>
          <RadioButtonGroup
            label="Is this case a Simulated Clinical Experience Case?"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={isSimulatedClinicalExperience}
            onChange={(value) => {
              setIsSimulatedClinicalExperience(value)
              onSave({
                isSimulatedClinicalExperience: value
              })
            }}
            error={isSimulatedClinicalExperienceError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <TextField
            label="Case Count"
            type="number"
            value={caseCount}
            onChange={(value) => setCaseCount(value)}
            onBlur={() => {
              onSave({
                caseCount: caseCount
              })
            }}
            error={caseCountError}
            disabled={true}
          />
        </div>
        <div className={styles.column20}>
          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={(value) => {
              setStartTime(value);
              onSave({
                startTime: value
              })
            }}
            error={startTimeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <TimePicker
            label="End Time"
            value={endTime}
            onChange={(value) => {
              setEndTime(value);
              onSave({
                endTime: value
              })
            }}
            error={endTimeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <TextField
            label="Patient’s Weight (Kilogram)"
            type="number"
            value={patientWeight}
            onChange={(value) => {
              const formattedWeight = formatWeight(value);
              setPatientWeight(formattedWeight);
            }}
            placeholder={'Enter in Kilogram'}
            onBlur={() => {
              onSave({
                patientWeight: patientWeight
              })
            }}
            error={patientWeightError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column33}>
          <MultiSelectDropdown
            label="Patient’s Position"
            options={patientPositionsList}
            selected={patientPosition}
            onChange={(value) => {
              if(value.length > 3) {
                showToast('You can select up to 3 positions only.', 'error')
              } else {
                setPatientPosition(value);
                onSave({
                  patientPosition: value
                });
              }
            }}
            error={patientPositionError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column33}>
          <MultiSelectDropdown
            label="Anatomic Location of Procedure"
            options={anatomicLocationOfProceduresList}
            selected={anatomicLocationOfProcedure}
            onChange={(value) => {
              if(value.length > 3) {
                showToast('You can select up to 3 only.', 'error')
              } else {
                setAnatomicLocationOfProcedure(value)
                onSave({
                  anatomicLocationOfProcedure: value
                });
              }
            }}
            error={anatomicLocationOfProcedureError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column33}>
          <Dropdown
            label="Mask Management"
            options={maskManagementList}
            value={maskManagement}
            onChange={(value) => {
              setMaskManagement(value)
              onSave({
                maskManagement: value
              });
            }}
            error={maskManagementError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column40}>
          <Dropdown
            label="Type of Anesthetic"
            options={typeOfAnestheticList}
            value={typeOfAnesthetic}
            onChange={(value) => {
              setTypeOfAnesthetic(value)
              onSave({
                typeOfAnesthetic: value
              })
            }}
            error={typeOfAnestheticError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column40}>
          <Dropdown
            label="Supra Glottic Airway Device"
            options={supraGlotticAirwayList}
            value={supraGlotticAirwayDevice}
            onChange={(value) => {
              setSupraGlotticAirwayDevice(value);
              onSave({
                supraGlotticAirwayDevice: value
              });
            }}
            error={supraGlotticAirwayDeviceError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <RadioButtonGroup
            label="Inhalation Induction"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={inhalationInduction}
            onChange={(value) => {
              setInhalationInduction(value)
              onSave({
                inhalationInduction: value
              })
            }}
            error={inhalationInductionError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <RadioButtonGroup
            label="Ventilation by Mask"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={ventilationByMask}
            onChange={(value) => {
              setVentilationByMask(value)
              onSave({
                ventilationByMask: value
              })
            }}
            error={ventilationByMaskError}
            disabled={disabled}
          />
        </div>
      </div>
    </form>
  );
});

CaseStartForm.displayName = 'CaseStartForm';

CaseStartForm.propTypes = {
  formData: PropTypes.object,
  patientPositionsList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  anatomicLocationOfProceduresList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      subOptions: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      )
    })
  ).isRequired,
  maskManagementList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  typeOfAnestheticList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  supraGlotticAirwayList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default CaseStartForm;